import { alpha } from '@mui/material/styles';
import { mobileAppGlassCard } from '../components/layout/mobile/mobileAppTheme';
import { THEME_COLORS } from '../shared/constants/theme';
import { DASHBOARD_ACCENT } from './dashboardSurfaceSx';

export const BOOKING_OPS_ACCENT = DASHBOARD_ACCENT;

export function getBookingOpsControlsLayoutSx(isMobile) {
  return {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    alignItems: isMobile ? 'stretch' : 'center',
    justifyContent: 'space-between',
    gap: isMobile ? 1.25 : 2,
    flexWrap: isMobile ? 'nowrap' : { xs: 'wrap', sm: 'nowrap' },
  };
}

export function getBookingOpsDateRowSx(isMobile) {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    width: isMobile ? '100%' : 'auto',
    justifyContent: isMobile ? 'center' : 'flex-start',
  };
}

export function getBookingOpsDatePickerBoxSx(isMobile) {
  return {
    minWidth: isMobile ? 0 : '200px',
    flex: isMobile ? 1 : 'none',
    maxWidth: isMobile ? 200 : 'none',
  };
}

export function getBookingOpsNavButtonSx(isDark, accent = BOOKING_OPS_ACCENT) {
  return {
    borderRadius: '10px',
    width: '36px',
    height: '36px',
    padding: 0,
    border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : alpha('#000', 0.08)}`,
    color: isDark ? '#e2e8f0' : '#334155',
    backgroundColor: isDark ? THEME_COLORS.SURFACE_HEADER_DARK : alpha('#000', 0.02),
    '&:hover': {
      backgroundColor: isDark ? alpha('#ffffff', 0.06) : alpha('#000', 0.04),
      borderColor: alpha(accent, isDark ? 0.28 : 0.35),
    },
  };
}

export function getBookingOpsDateFieldSx(isDark, accent = BOOKING_OPS_ACCENT) {
  return {
    '& .MuiOutlinedInput-root': {
      height: '36px',
      fontSize: '14px',
      backgroundColor: isDark ? THEME_COLORS.SURFACE_HEADER_DARK : '#fff',
      '& fieldset': {
        borderColor: isDark ? alpha('#ffffff', 0.1) : alpha('#000', 0.12),
      },
      '&:hover fieldset': {
        borderColor: alpha(accent, isDark ? 0.28 : 0.35),
      },
      '&.Mui-focused fieldset': {
        borderColor: alpha(accent, isDark ? 0.42 : 0.5),
      },
    },
    '& .MuiInputBase-input': {
      padding: '8px 12px',
      textAlign: 'center',
      color: isDark ? '#e2e8f0' : '#0f172a',
    },
  };
}

export function getBookingOpsFiltersRowSx(isMobile) {
  return {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    alignItems: isMobile ? 'stretch' : 'center',
    gap: isMobile ? 1 : 2,
    width: isMobile ? '100%' : 'auto',
    minWidth: 0,
  };
}

export function getBookingOpsFilterToggleSx(isMobile, isDark, accent = BOOKING_OPS_ACCENT) {
  return {
    width: isMobile ? '100%' : 'auto',
    maxWidth: isMobile ? '100%' : 'none',
    display: 'inline-flex',
    flexWrap: 'nowrap',
    overflowX: isMobile ? 'auto' : 'visible',
    overflowY: 'hidden',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    py: 0.2,
    px: 0.2,
    borderRadius: '10px',
    border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : alpha('#000', 0.08)}`,
    background: isDark ? THEME_COLORS.SURFACE_HEADER_DARK : alpha('#000', 0.02),
    '& .MuiToggleButtonGroup-grouped': {
      border: 0,
      borderRadius: '8px !important',
      mx: 0.1,
      flex: isMobile ? '0 0 auto' : undefined,
    },
    '& .MuiToggleButton-root': {
      border: 0,
      color: isDark ? alpha('#f8fafc', 0.45) : alpha('#0f172a', 0.45),
      fontSize: isMobile ? '0.7rem' : '0.875rem',
      fontWeight: 700,
      padding: isMobile ? '6px 10px' : '4px 12px',
      textTransform: 'none',
      lineHeight: 1.2,
      whiteSpace: 'nowrap',
      letterSpacing: '0.02em',
      '&.Mui-selected': {
        backgroundColor: `${alpha('#ffffff', isDark ? 0.08 : 0.1)} !important`,
        color: `${isDark ? '#e2e8f0' : '#0f172a'} !important`,
        boxShadow: `inset 0 0 0 1px ${alpha(accent, isDark ? 0.22 : 0.28)}`,
        '&:hover': {
          backgroundColor: `${alpha('#ffffff', isDark ? 0.1 : 0.12)} !important`,
        },
      },
      '&:hover': {
        backgroundColor: isDark ? alpha('#ffffff', 0.04) : alpha('#000', 0.03),
      },
    },
  };
}

export function getBookingOpsTotalRowSx(isMobile, isDark = true) {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: isMobile ? 'space-between' : 'flex-start',
    gap: 1,
    width: isMobile ? '100%' : 'auto',
    ...(isMobile
      ? {
          pt: 0.75,
          borderTop: `1px solid ${isDark ? alpha('#ffffff', 0.08) : alpha('#000', 0.08)}`,
        }
      : {}),
  };
}

export function getBookingOpsTotalLabelSx(isDark) {
  return {
    color: isDark ? alpha('#cbd5e1', 0.72) : '#64748b',
    fontWeight: 600,
    fontSize: '0.875rem',
  };
}

export function getBookingOpsTotalValueSx(isDark) {
  return {
    color: isDark ? '#f8fafc' : '#0f172a',
    fontWeight: 800,
    fontSize: '1.05rem',
    letterSpacing: '-0.02em',
  };
}

export function getBookingOpsGridItemSx() {
  return {
    display: 'flex',
  };
}

export function getBookingOpsBookingCardSx(isMobile, isDark, accent = BOOKING_OPS_ACCENT) {
  if (isMobile) {
    return {
      ...mobileAppGlassCard,
      cursor: 'pointer',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      transition: 'border-color 0.2s ease',
      '&:active': {
        borderColor: accent,
      },
    };
  }

  return {
    cursor: 'pointer',
    background: isDark
      ? `linear-gradient(165deg, ${THEME_COLORS.SURFACE_ELEVATED_DARK} 0%, ${THEME_COLORS.BACKGROUND_DARK} 100%)`
      : 'linear-gradient(165deg, #ffffff 0%, #f8fafc 100%)',
    border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : alpha('#000', 0.08)}`,
    borderRadius: 2,
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: isDark
      ? '0 12px 28px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.04)'
      : '0 8px 20px rgba(15,23,42,0.08)',
    '&:hover': {
      borderColor: alpha(accent, isDark ? 0.32 : 0.4),
      transform: 'translateY(-4px)',
      boxShadow: isDark
        ? `0 16px 32px rgba(0,0,0,0.34), inset 0 0 0 1px ${alpha(accent, 0.14)}`
        : `0 12px 24px rgba(15,23,42,0.12), inset 0 0 0 1px ${alpha(accent, 0.18)}`,
    },
  };
}

export function getStayFilterLabel(value, isMobile) {
  if (!isMobile) {
    switch (value) {
      case 'pending_checkout':
        return 'Pending Checkout';
      case 'continuing':
        return 'Continuing';
      case 'booked':
        return 'Booked';
      case 'cancelled':
        return 'Cancelled';
      case 'all':
        return 'All';
      default:
        return value;
    }
  }

  switch (value) {
    case 'pending_checkout':
      return 'Pending';
    case 'continuing':
      return 'Continuing';
    case 'booked':
      return 'Booked';
    case 'cancelled':
      return 'Cancelled';
    case 'all':
      return 'All';
    default:
      return value;
  }
}
